import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { DataTable } from "./data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { useSocket } from "@/contexts/SocketContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import type { ImpressaoItem } from "@/types/impressao-item";
import { alert } from "@/contexts/AlertModalContext";
import {
  serializarTratativas,
  tratativaAtiva,
} from "@/lib/tratativasImpressao";
import {
  AlertTriangle,
  Check,
  Clock,
  Download,
  Edit3,
  Loader2,
  Pause,
  Play,
  Printer,
  RotateCw,
  Tag as TagIcon,
  Trash2,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  baixarArquivoGerado,
  baixarArquivosLegacy,
  downloadImpressaoLegacy,
  type ArquivosImpressaoLegacy,
} from "@/utils/UtilFuncoes";
import { useGlobalLoader } from "./GlobalLoader";
import {
  dividirDownloadGraficoEmPacotes,
  normalizarDownloadsGraficos,
} from "@/utils/zplDesigner";
import ZebraBrowserPrintWrapper from "zebra-browser-print-wrapper";
import { useItem } from "@/contexts/ItemContext";
import { useOrdensDeImpressao } from "@/contexts/OrdensDeImpressaoContext";
import type { newHistoricoItem } from "@/types/new-historico-item";
import { useHistorico } from "@/contexts/HistoricoContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import type { historicoItemResponse } from "@/types/historico-item-response-";
import { useVerificarImpressao } from "@/contexts/VerificarImpressaoContext";
import { ModalItensNaoInventariados } from "./ModalItensNaoInventariados";
import { buscarItensNaoInventariados } from "@/services/itensNaoInventariados";
import {
  MODOS_IMPRESSAO,
  resolverCanalImpressao,
  type ModoImpressao,
} from "@/lib/modosImpressao";
import axios from "axios";

type ImpressaoContextType = {
  adicionarItensNaFila: (
    itens: ImpressaoItem[],
    nome_da_ordem?: string,
    reimpressao?: boolean,
    modoImpressao?: ModoImpressao,
    detalheModo?: string,
  ) => void;
  fila: ImpressaoItem[];
  estaImprimindo: boolean;
  tagsImpressasNum: React.MutableRefObject<number>;
  toggleImpressaoPausada: () => void;
  impressaoPausada: boolean;
  lerEtiqueta: () => void;
  finalizarImpressao: (sucesso?: boolean) => void;
  /** Descarta o lote que ainda não foi para a base (modo "salvar no final").
   *  Usado ao cancelar a impressão. */
  descartarSalvamentoPendente: () => void;
  reimprimirItem: () => void;
  regravarEtiqueta: () => void;
  deuErro: boolean;
  erroMensagem: string;
  cardConfirmarImpressao: boolean;
  setCardConfirmarImpressao: (value: boolean) => void;
  handleAlterarItens: () => void;
  cardAlterarItens: boolean;
  setFila: React.Dispatch<React.SetStateAction<ImpressaoItem[]>>;
  setItensParaRemocao: React.Dispatch<React.SetStateAction<ImpressaoItem[]>>;
  itensConfirmadosParaRemover: ImpressaoItem[];
  setItensConfirmadosParaRemover: React.Dispatch<
    React.SetStateAction<ImpressaoItem[]>
  >;
  itensParaRemocao: ImpressaoItem[];
  /** Arquivos da ultima ordem legacy, pra tela oferecer o download manual
   *  quando o automatico nao acontecer. */
  arquivosLegacy: ArquivosImpressaoLegacy | null;
};

const ImpressaoContext = createContext<ImpressaoContextType | undefined>(
  undefined,
);

export function ImpressaoProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { setIsLoading } = useGlobalLoader();
  const { definirOrdemComoImpressa } = useOrdensDeImpressao();
  const { impressaoConfig } = useImpressaoConfig();
  const [estaImprimindo, setEstaImprimindo] = useState(false);
  const [fila, setFila] = useState<ImpressaoItem[]>([]);
  const { isConnected, lastMessage, enviarMensagem } = useSocket();
  const [impressaoPausada, setImpressaoPausada] = useState(false);
  const indiceAtual = useRef(0);
  // true entre o envio de um item ao modulo e a chegada do resultado dele
  // (imprimiu/gravou/erro/GRRV). Resultado ou "continuar impressao" que chega
  // sem item aguardando e atrasado/duplicado e nao pode reimprimir nada.
  const aguardandoResultado = useRef(false);
  const tentativas = useRef(0);
  const tagsImpressasNum = useRef(0);
  const removeuLogo = useRef(false);
  // BLALL: varredura de tags virgens "soltas" sob a antena antes da ordem.
  // Enviado uma vez por ordem; aguardamos a resposta (firmware >= 2.07) antes
  // de iniciar a fila. Firmwares antigos nao respondem -- caimos no timeout
  // e seguimos a impressao normalmente.
  const blallEnviado = useRef(false);
  const blallExecutado = useRef(false);
  const blallTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const BLALL_TIMEOUT_MS = 2500;
  // CLRQ: limpa a fila de impressao do firmware no inicio de cada ordem.
  // Descarta defeitos (ou outros itens) orfaos de ordens anteriores que
  // ainda nao foram drenados. Fire-and-forget: firmwares < 2.07 ignoram
  // o comando desconhecido. Suportado a partir do firmware 2.07.
  const clrqEnviado = useRef(false);
  const [deuErro, setDeuErro] = useState(false);
  const [erroMensagem, setErroMensagem] = useState("");
  const [cardConfirmarImpressao, setCardConfirmarImpressao] = useState(false);
  const [arquivosLegacy, setArquivosLegacy] =
    useState<ArquivosImpressaoLegacy | null>(null);
  const [cardAlterarItens, setCardAlterarItens] = useState(false);
  // Trava "Exigir inventario": bloqueia impressao enquanto houver itens
  // impressos e nao inventariados (ClickUp 86ahz47jg).
  const [modalInventarioAberto, setModalInventarioAberto] = useState(false);
  const { salvarItensNaBase } = useItem();
  const [nome_da_ordem, setNomeDaOrdem] = useState<string | undefined>(
    undefined,
  );
  const { criarHistorico, atualizarHistorico } = useHistorico();
  const [criouHistorico, setCriouHistorico] = useState(false);
  const { categorias } = useCategorias();
  const { localizacoes } = useLocalizacoes();
  const [itensParaRemocao, setItensParaRemocao] = useState<ImpressaoItem[]>([]);
  const [itensConfirmadosParaRemover, setItensConfirmadosParaRemover] =
    useState<ImpressaoItem[]>([]);
  const [historicoCriado, setHistoricoCriado] = useState<
    historicoItemResponse | undefined
  >();
  const [erros, setErros] = useState<string[]>([]);
  const { verificarImpressao } = useVerificarImpressao();
  const [reimpressao, setReimpressao] = useState(false);
  // Itens que foram impressos fisicamente mas falharam ao salvar/atualizar
  // serial no backend após retries. Mostrados num toast consolidado ao final.
  // Itens já impressos que ainda não foram para a base. Só enche quando a
  // conta escolhe salvar no fim da impressão; no modo por etiqueta cada item
  // é gravado na hora e a lista fica sempre vazia.
  const itensPendentesRef = useRef<ImpressaoItem[]>([]);
  const falhasSalvamentoRef = useRef<
    { tag: string; codigo: string; motivo: string }[]
  >([]);

  // --- Status da impressora (ST/INFO) antes de imprimir ---
  // Envia ST e aguarda a resposta INFO do firmware. O campo [7] do INFO e
  // "SIM"/"NAO" = impressora Zebra USB detectada pelo modulo (temImpressora).
  // Usado como trava fail-closed em adicionarItensNaFila: sem resposta dentro
  // do timeout, a impressora e considerada indisponivel.
  const ST_TIMEOUT_MS = 3000;
  const stPendenteRef = useRef<{
    timeout: ReturnType<typeof setTimeout>;
    resolve: (data: string | null) => void;
  } | null>(null);

  const aguardarStatusImpressora = (): Promise<string | null> =>
    new Promise((resolve) => {
      // Cancela qualquer espera pendente anterior (so a ultima interessa).
      if (stPendenteRef.current) {
        clearTimeout(stPendenteRef.current.timeout);
        stPendenteRef.current.resolve(null);
      }
      const entry = {
        timeout: setTimeout(() => {
          if (stPendenteRef.current === entry) stPendenteRef.current = null;
          resolve(null);
        }, ST_TIMEOUT_MS),
        resolve: (data: string | null) => {
          clearTimeout(entry.timeout);
          if (stPendenteRef.current === entry) stPendenteRef.current = null;
          resolve(data);
        },
      };
      stPendenteRef.current = entry;
      enviarMensagem("ST");
    });

  // Resolve a espera de status quando o INFO chega. Independente de
  // estaImprimindo (o gate roda antes de a fila comecar).
  useEffect(() => {
    if (!lastMessage?.data) return;
    if (!stPendenteRef.current) return;
    if (String(lastMessage.data).includes("INFO")) {
      stPendenteRef.current.resolve(String(lastMessage.data));
    }
  }, [lastMessage]);

  const handleAtivarCardConfirmarImpressao = async () => {
    await descarregarSalvamentoPendente();
    await verificarImpressao(fila.slice(0, tagsImpressasNum.current));
    setCardConfirmarImpressao(true);
  };

  const handleAlterarItens = () => {
    setCardAlterarItens(true);
    setCardConfirmarImpressao(false);
  };

  const toggleImpressaoPausada = () => {
    setImpressaoPausada((prev) => !prev);
  };

  const lerEtiqueta = () => {
    enviarMensagem("LEA");
  };

  // Monta a string de observação do histórico: modo de impressão (ex.:
  // "Impressão Avulsa", "Impressão Pedido - OF: 123") + resumo dos dados
  // (qtd de etiquetas e localização). Aparece nos Logs de Impressão.
  // O modo também vai gravado em campo próprio (modo_impressao); aqui ele
  // continua na observação só para manter a legibilidade do texto corrido.
  const montarObservacao = (
    modoImpressao: string | undefined,
    itens: ImpressaoItem[],
  ) => {
    const primeiro = itens[0];
    const local = primeiro
      ? [
          localizacoes.find((l) => l._id === primeiro.id_localizacao1)
            ?.descricao,
          localizacoes
            .flatMap((l) => l.children)
            .find((l2) => l2._id === primeiro.id_localizacao2)?.descricao,
          localizacoes
            .flatMap((l) => l.children.flatMap((l2) => l2.children))
            .find((l3) => l3._id === primeiro.id_localizacao3)?.descricao,
          localizacoes
            .flatMap((l) => l.children.flatMap((l2) => l2.children))
            .flatMap((l3) => l3.children)
            .find((l4) => l4._id === primeiro.id_localizacao4)?.descricao,
        ]
          .filter(Boolean)
          .join(" > ")
      : "";
    const linhas = [modoImpressao || "Impressão", `Etiquetas: ${itens.length}`];
    if (local) linhas.push(`Local: ${local}`);
    return linhas.join("\n");
  };

  const adicionarItensNaFila = async (
    itens: ImpressaoItem[],
    nome_da_ordem?: string,
    reimpressao?: boolean,
    modoImpressao?: ModoImpressao,
    detalheModo?: string,
  ) => {
    try {
      // Trava "impressora apta": antes de qualquer impressao consulta o status
      // do modulo (ST) e bloqueia se a Zebra USB nao estiver detectada (campo
      // temImpressora do INFO). Fail-closed: sem resposta = indisponivel.
      // Vale para todos os fluxos pois todos passam por esta funcao.
      //
      // Nao se aplica aos modos USB e legacy: eles imprimem sem passar pelo
      // firmware do modulo (nao ha ST/INFO), entao o check e desativado.
      const usaModulo =
        impressaoConfig?.impressao_usb !== "true" &&
        impressaoConfig?.impressao_legacy !== "true";
      if (usaModulo) {
        const infoStatus = await aguardarStatusImpressora();
        const impressoraApta =
          !!infoStatus &&
          infoStatus.split("|")[7]?.trim().toUpperCase() === "SIM";
        if (!impressoraApta) {
          alert.error(
            infoStatus
              ? "A impressora não está disponível para impressão. Verifique se ela está ligada e conectada ao módulo."
              : "Não foi possível verificar o status da impressora — o módulo não respondeu. Verifique a conexão e tente novamente.",
            { title: "Impressora indisponível" },
          );
          return;
        }
      }

      // Trava "Exigir inventario": antes de qualquer impressao, se a preferencia
      // estiver ligada, bloqueia caso existam itens impressos e nao inventariados.
      // Vale para todos os fluxos pois todos passam por esta funcao.
      if (impressaoConfig?.exigir_inventario && impressaoConfig?.id_usuario) {
        let bloquear = false;
        try {
          const resp = await buscarItensNaoInventariados({
            id_usuario: impressaoConfig.id_usuario,
            page: 0,
            pageSize: 1,
          });
          bloquear = (resp?.total ?? 0) > 0;
          if (bloquear) {
            alert.warning(
              "Existem etiquetas impressas que ainda não foram inventariadas. Conclua o inventário antes de imprimir novamente.",
              { title: "Impressão bloqueada" },
            );
          }
        } catch {
          // fail-closed: falha na rota bloqueia a impressao por seguranca.
          bloquear = true;
          alert.error(
            "Não foi possível verificar o inventário. Por segurança, a impressão foi bloqueada.",
            { title: "Impressão bloqueada" },
          );
        }
        if (bloquear) {
          setModalInventarioAberto(true);
          return;
        }
      }

      if (reimpressao) {
        setReimpressao(true);
      }
      if (!criouHistorico) {
        const modo = modoImpressao || MODOS_IMPRESSAO.NAO_INFORMADO;
        const modoComDetalhe = detalheModo ? `${modo} - ${detalheModo}` : modo;
        const newHistorico: newHistoricoItem = {
          campo_descricao_ordem_impressao:
            impressaoConfig?.campo_descricao_ordem_impressao || "-",
          etiqueta_padrao: impressaoConfig?.etiqueta_padrao || "-",
          hardware_id: impressaoConfig?.app_id || "-",
          id_conta: impressaoConfig?.id_conta || "-",
          id_usuario: impressaoConfig?.id_usuario || "-",
          nome_da_ordem:
            nome_da_ordem ||
            `impressaoWeb-${new Date().toISOString()}-${Math.random()
              .toString(36)
              .substring(2, 15)}-${impressaoConfig?.id_conta}`,
          ordem_itens_tratados: itens.map((item) => ({
            quantidade: item.quantidade || "1",
            valor: item.valor || 0,
            zpl: item.zpl_impressao || "",
            _id: item._id || "",
            codigo:
              categorias.find((cat) => cat._id === item.id_categoria)?.ean ||
              item.codigo,
            descricao:
              categorias.find((cat) => cat._id === item.id_categoria)
                ?.descricao || item.descricao,
            dt_validade: item.dt_validade || "",
            epc: item.tag || "",
            id_externo: "",
            inf_compl_1: item.inf_compl_1 || "",
            inf_compl_2: item.inf_compl_2 || "",
            inf_compl_3: item.inf_compl_3 || "",
            inf_compl_4: item.inf_compl_4 || "",
            inf_compl_5: item.inf_compl_5 || "",
            inf_compl_6: item.inf_compl_6 || "",
            inf_compl_7: item.inf_compl_7 || "",
            inf_compl_8: item.inf_compl_8 || "",
            inf_compl_9: item.inf_compl_9 || "",
            inf_compl_10: item.inf_compl_10 || "",
            inf_compl_11: "",
            inf_compl_12: "",
            localizacao_1:
              localizacoes.find((l) => l._id === item.id_localizacao1)
                ?.descricao || "",
            localizacao_2:
              localizacoes
                .flatMap((l) => l.children)
                .find((l2) => l2._id === item.id_localizacao2)?.descricao ||
              "-",
            localizacao_3:
              localizacoes
                .flatMap((l) => l.children.flatMap((l2) => l2.children))
                .find((l3) => l3._id === item.id_localizacao3)?.descricao ||
              "-",
            localizacao_4:
              localizacoes
                .flatMap((l) => l.children.flatMap((l2) => l2.children))
                .flatMap((l3) => l3.children)
                .find((l4) => l4._id == item.id_localizacao4)?.descricao || "-",
          })),
          padrao_epc: impressaoConfig?.padrao_epc || "",
          posicao_gtin_impressao:
            impressaoConfig?.posicao_gtin_impressao || "-",
          potencia_padrao: impressaoConfig?.potencia_padrao || "2",
          estado_da_ordem: "Finalizado",
          observacao: montarObservacao(modoComDetalhe, itens),
          modo_impressao: modo,
          detalhe_modo_impressao: detalheModo || "",
          canal_impressao: resolverCanalImpressao(impressaoConfig),
        };
        setCriouHistorico(true);
        const novoHistoricoCriado = await criarHistorico(newHistorico);
        setHistoricoCriado(novoHistoricoCriado);
      }
    } catch (error) {
      finalizarImpressao(false);
      return;
    }
    if (nome_da_ordem) {
      setNomeDaOrdem(nome_da_ordem);
    }
    setFila((prev) => [...prev, ...itens]);
  };

  useEffect(() => {
    if (!estaImprimindo && fila.length > 0 && isConnected) {
      if (!clrqEnviado.current) {
        clrqEnviado.current = true;
        enviarMensagem("CLRQ");
      }
      const ehSocketRfid =
        impressaoConfig?.impressao_usb !== "true" &&
        impressaoConfig?.impressao_legacy !== "true" &&
        !impressaoConfig?.gravacao_reversa;
      if (ehSocketRfid && !blallExecutado.current) {
        if (!blallEnviado.current) {
          blallEnviado.current = true;
          setEstaImprimindo(true);
          enviarMensagem("BLALL");
          if (!impressaoConfig?.apenas_gravar) {
            // alert.success vai pra toast auto-dismiss (bottom-right);
            // alert.info abriria modal bloqueante.
            alert.success(
              "Verificando etiquetas no campo antes de iniciar a ordem...",
              { title: "Aguarde" },
            );
          }
          // Firmwares < 2.07 nao respondem ao BLALL. Sem este fallback a
          // fila ficaria parada esperando pra sempre.
          blallTimeoutId.current = setTimeout(() => {
            if (blallExecutado.current) return;
            blallExecutado.current = true;
            blallTimeoutId.current = null;
            if (fila.length > 0 && isConnected) {
              imprimirItem(fila[indiceAtual.current]);
            }
          }, BLALL_TIMEOUT_MS);
        }
        return;
      }
      imprimirItem(fila[indiceAtual.current]);
    }
  }, [fila, estaImprimindo, isConnected, impressaoConfig]);

  const marcarDefeito = (defeito: string) => {
    if (impressaoConfig?.zpl_marcacao_defeito) {
      let zplMarcacaoDefeito = impressaoConfig.zpl_marcacao_defeito;
      zplMarcacaoDefeito = zplMarcacaoDefeito.replace(
        "{erro}",
        defeito || "N/A",
      );
      enviarMensagem(formarPacoteParaEnvio(zplMarcacaoDefeito, true));
    }
  };

  useEffect(() => {
    if (!lastMessage) return;
    if (!estaImprimindo) return;

    const processarMensagem = async () => {
      const resposta = lastMessage.data.toLowerCase();

      // Resposta do BLALL: libera o início da fila independentemente de
      // OK/BUSY/NOMOD — o firmware ja faz a varredura ou explica por que nao.
      if (resposta.startsWith("blall|")) {
        // Guarda contra double-fire: se o timeout (ou uma resposta anterior)
        // ja iniciou a impressao, ignora resposta tardia/duplicada.
        if (blallExecutado.current) return;
        blallExecutado.current = true;
        if (blallTimeoutId.current) {
          clearTimeout(blallTimeoutId.current);
          blallTimeoutId.current = null;
        }
        if (fila.length > 0 && isConnected) {
          await imprimirItem(fila[indiceAtual.current]);
        }
        return;
      }

      const itemAtual = fila[indiceAtual.current];

      if (resposta.includes("no printer")) {
        alert.error("Impressora não conectada ou indisponível.");
        return;
      }

      if (resposta.includes("rlea")) {
        let epcLido = resposta.split("+")[1];
        if (!epcLido) {
          alert.error(t("impressao.tagNotFound"));
          return;
        }
        epcLido = epcLido.trim();
        alert.info(`Etiqueta lida: ${epcLido}`);
        return;
      }

      if (resposta.includes("continuar")) {
        if (impressaoPausada) {
          return;
        }
        // O modulo manda "continuar impressao" a cada (re)conexao do socket.
        // So reenvia se o item atual ainda esta sem resultado (modulo caiu no
        // meio do job); se o resultado ja chegou, reenviar duplicaria a
        // etiqueta.
        if (!aguardandoResultado.current) return;
        if (indiceAtual.current < fila.length) {
          await imprimirItem(fila[indiceAtual.current]);
        }
        return;
      }

      const ehResultadoDoItem =
        resposta.includes("imprimiu") ||
        resposta.includes("gravou") ||
        resposta.includes("erro") ||
        resposta.includes("grrv-");
      if (ehResultadoDoItem) {
        if (!aguardandoResultado.current) {
          console.warn(
            "[impressao] resultado ignorado: nenhum item aguardando resposta",
            resposta,
          );
          return;
        }
        aguardandoResultado.current = false;
      }

      if (impressaoConfig?.gravacao_reversa) {
        if (resposta.includes("grrv-")) {
          const epc: string = resposta
            .replace("grrv-", "")
            .trim()
            .toUpperCase();
          const epcJaUsado = fila.some(
            (item) => item.tag.toUpperCase() === epc.toUpperCase(),
          );
          const maxTentativas =
            impressaoConfig?.tratativa_automatica_tentativas_ordem ?? 0;
          if (epcJaUsado) {
            if (maxTentativas > 0 && tentativas.current < maxTentativas) {
              alert.warning(`EPC ${epc} já usado. Tentando regravar...`);
              tentativas.current++;
              marcarDefeito("EPC JA ESTA EM USO");
              setErros((prev) => [...prev, `${epc}: EPC JA ESTA EM USO`]);
              regravarEtiqueta();
              return;
            } else {
              setDeuErro(true);
              let erroMsg = `EPC ${epc} já está em uso.`;
              alert.error(erroMsg);
              setErroMensagem(erroMsg);
              return;
            }
          }
          fila[indiceAtual.current].tag = epc.toUpperCase();
          enviarMensagem("BL+" + epc.toUpperCase());

          await salvarNaBase([fila[indiceAtual.current]]);
          indiceAtual.current++;
          tagsImpressasNum.current++;

          if (indiceAtual.current >= fila.length) {
            handleAtivarCardConfirmarImpressao();
          } else {
            await imprimirItem(fila[indiceAtual.current]);
          }
          return;
        }
      }

      if (resposta.includes("imprimiu") || resposta.includes("gravou")) {
        enviarMensagem("BL+" + itemAtual.tag.toUpperCase());
        await salvarNaBase([itemAtual]);
        indiceAtual.current++;
        tentativas.current = 0;
        tagsImpressasNum.current++;

        if (indiceAtual.current < fila.length) {
          await imprimirItem(fila[indiceAtual.current]);
        } else {
          handleAtivarCardConfirmarImpressao();
        }
      } else if (resposta.includes("erro")) {
        const ehNenhumaTagEncontrada = resposta.includes(
          "erro gravar nenhuma tag encontrada",
        );
        // Itens vindos de Gravação Avulsa (ou afins) marcam
        // `pular_marcacao_defeito_override` — etiqueta é virgem, e imprimir o
        // marcador de defeito sujaria o visual. Pula auto-retry e marcarDefeito;
        // operador clica em "Regravar" manualmente.
        const pularMarcacaoDefeito =
          itemAtual?.pular_marcacao_defeito_override ?? false;
        const maxTentativas = pularMarcacaoDefeito
          ? 0
          : (impressaoConfig?.tratativa_automatica_tentativas_ordem ?? 0);
        if (maxTentativas > 0 && tentativas.current < maxTentativas) {
          tentativas.current++;
          const motivoDefeito = ehNenhumaTagEncontrada
            ? "NENHUMA TAG VIRGEM ENCONTRADA"
            : "ERRO AO GRAVAR";
          marcarDefeito(motivoDefeito);
          setErros((prev) => [
            ...prev,
            ehNenhumaTagEncontrada
              ? `${itemAtual.tag}: NENHUMA TAG VIRGEM ENCONTRADA`
              : `${itemAtual.tag}: EPC JA ESTA EM USO`,
          ]);
          await imprimirItem(itemAtual);
        } else {
          setDeuErro(true);
          const sufixoTentativas =
            maxTentativas > 0
              ? ` após ${maxTentativas} tentativa${maxTentativas > 1 ? "s" : ""}`
              : "";
          let erroMsg = resposta;
          if (ehNenhumaTagEncontrada) {
            erroMsg = `Nenhuma etiqueta encontrada para gravação${sufixoTentativas}.`;
            if (!impressaoConfig?.apenas_gravar) alert.error(erroMsg);
          } else if (
            resposta.includes("erro gravar nao foi possivel gravar a etiqueta")
          ) {
            erroMsg = `Erro ao gravar etiqueta ${itemAtual.tag}${sufixoTentativas}.`;
            alert.error(erroMsg);
          } else {
            erroMsg = `Erro desconhecido${sufixoTentativas}: ${resposta}`;
            alert.error(erroMsg);
          }
          setErroMensagem(erroMsg);
        }
      }
    };

    processarMensagem();
  }, [lastMessage]);

  const formarPacoteParaEnvio = (
    pacote: string,
    defeito: boolean = false,
    epcOverride?: string,
  ) => {
    const itemAtual = fila[indiceAtual.current];
    const zplPadrao =
      impressaoConfig?.lista_zpl[
        parseInt(impressaoConfig?.etiqueta_padrao || "0")
      ]?.zpl;
    let delay = impressaoConfig?.tempo_delay_gravacao || "720";
    let tentativasGravacao = impressaoConfig?.tentativas_gravacao || "1";
    let forcaPadrao = impressaoConfig?.potencia_padrao || "2";
    let inicioTagVirgem =
      itemAtual?.inicio_tag_virgem_override ||
      impressaoConfig?.inicio_tag_virgem ||
      "E280";
    let priorizarMaiorEtiqueta = impressaoConfig?.tag_virgem_maior_epc
      ? "true"
      : "false";
    let socketPuro = "false";
    let idSocketPuro = "-";
    let apenasGravar = impressaoConfig?.apenas_gravar ? "true" : "false";
    let travarEtiqueta = "false";
    let primeiraDaOrdem = "false";
    let conferirEtiquetaAposGravacao = tratativaAtiva(
      impressaoConfig,
      "conferir_tag_apos_gravar",
    )
      ? "true"
      : "false";
    // Campo extra (sempre no fim do pacote): estado das tratativas de
    // gravação. Firmware antigo ignora o token; firmware 2.26+ usa.
    let tratativas = serializarTratativas(impressaoConfig);
    let temRfid = zplPadrao?.includes("RFW") ? "true" : "false";

    let pacoteFormatado = "";
    if (defeito == true) {
      pacoteFormatado = `-${pacote}|${temRfid}|FFFFFFFFFFFFFFFFFFFFFFFF|${delay}|${apenasGravar}|${socketPuro}|${idSocketPuro}|${travarEtiqueta}|${tentativasGravacao}|${primeiraDaOrdem}|${inicioTagVirgem}|${priorizarMaiorEtiqueta}|${forcaPadrao}|${conferirEtiquetaAposGravacao}|${tratativas}`;
    } else if (impressaoConfig?.gravacao_reversa) {
      pacoteFormatado = `*${pacote}|${delay}|${tentativasGravacao}|${inicioTagVirgem}|${priorizarMaiorEtiqueta}|${forcaPadrao}|${socketPuro}|${idSocketPuro}`;
    } else {
      let epc = epcOverride ?? fila[indiceAtual.current]?.tag ?? "";
      pacoteFormatado = `+${pacote}|${temRfid}|${epc}|${delay}|${apenasGravar}|${socketPuro}|${idSocketPuro}|${travarEtiqueta}|${tentativasGravacao}|${primeiraDaOrdem}|${inicioTagVirgem}|${priorizarMaiorEtiqueta}|${forcaPadrao}|${conferirEtiquetaAposGravacao}|${tratativas}`;
    }

    return pacoteFormatado;
  };

  const reimprimirItem = () => {
    if (impressaoPausada) return;
    if (indiceAtual.current < fila.length) {
      imprimirItem(fila[indiceAtual.current]);
    } else {
      alert.error(t("impressao.noItemToReprint"));
    }
  };

  const regravarEtiqueta = () => {
    if (impressaoPausada) return;
    if (indiceAtual.current < fila.length) {
      const itemAtual = fila[indiceAtual.current];
      if (itemAtual.zpl_impressao) {
        let zpl_vazio = "^XA^XZ";
        let itemAtualSemZpl: ImpressaoItem = {
          ...itemAtual,
        };
        itemAtualSemZpl.zpl_impressao = zpl_vazio;
        imprimirItem(itemAtualSemZpl);
      } else {
        alert.error(t("impressao.noZplToRewrite"));
      }
    } else {
      alert.error(t("impressao.noItemToRewrite"));
    }
  };

  const imprimirItem = async (item: ImpressaoItem) => {
    if (impressaoPausada) return;
    setDeuErro(false);
    setEstaImprimindo(true);
    // ~DG escrito dentro do ^XA nao grava na impressora; sobe pra fora antes
    const zplNormalizado = normalizarDownloadsGraficos(item.zpl_impressao);
    const logo = zplNormalizado.split("^XA")[0];
    const zplSemLogo = "^XA" + zplNormalizado.split("^XA")[1] || "";

    const zplLimpo = zplSemLogo
      .replace(/\^XA/g, "")
      .replace(/\^XZ/g, "")
      .trim();

    if (impressaoConfig?.impressao_usb == "true") {
      try {
        if (impressaoConfig?.gravacao_reversa) {
          alert.error(t("impressao.usbNotReverseCompatible"));
          finalizarImpressao(false);
          return;
        }
        const browserPrint = new ZebraBrowserPrintWrapper();
        const defaultPrinter = await browserPrint.getDefaultPrinter();
        browserPrint.setPrinter(defaultPrinter);
        const printerStatus = await browserPrint.checkPrinterStatus();
        if (printerStatus.isReadyToPrint) {
          if (!removeuLogo.current && logo.length) {
            browserPrint.print(logo);
            removeuLogo.current = true;
          }
          for (let i = 0; i < fila.length; i++) {
            let zplSemLogoUsb =
              normalizarDownloadsGraficos(fila[i].zpl_impressao).split(
                "^XA",
              )[1] || "";
            zplSemLogoUsb = zplSemLogoUsb.replace(/\^XZ/g, "").trim();
            await browserPrint.print(`^XA${zplSemLogoUsb}^XZ`);
            indiceAtual.current++;
            tagsImpressasNum.current++;
            handleAtivarCardConfirmarImpressao();
          }
          return true;
        } else {
          throw new Error(
            `Erro ao imprimir via USB: Impressão não está pronta.`,
          );
        }
      } catch (error) {
        if (error == "Error: TypeError: Failed to fetch") {
          alert.error(
            "Erro ao conectar com a impressora USB. Verifique a conexão.",
          );
        } else {
          alert.error("Erro ao imprimir via USB: " + error);
        }

        finalizarImpressao(false);
        return;
      }
    }
    if (impressaoConfig?.impressao_legacy == "true") {
      if (impressaoConfig?.gravacao_reversa) {
        alert.error(t("impressao.legacyNotReverseCompatible"));
        finalizarImpressao(false);
        return;
      }

      let txtLegacy = "";
      if (!removeuLogo.current && logo.length) {
        txtLegacy += `${logo}\n\n`;
        removeuLogo.current = true;
      }
      for (let i = 0; i < fila.length; i++) {
        const zplNormalizadoLegacy = normalizarDownloadsGraficos(
          fila[i].zpl_impressao,
        );
        let zplSemLogoLegacy = zplNormalizadoLegacy.split("^XA")[1] || "";
        zplSemLogoLegacy = zplSemLogoLegacy.replace(/\^XZ/g, "").trim();
        let zplLimpoLegacy = ("^XA" + (zplNormalizadoLegacy.split("^XA")[1] || ""))
          .replace(/\^XA/g, "")
          .replace(/\^XZ/g, "");
        txtLegacy += `^XA\n`;
        if (i == 0 && impressaoConfig?.comando_zpl_inicial) {
          txtLegacy += `${impressaoConfig.comando_zpl_inicial}\n`;
        }
        if (
          i > 0 &&
          i < fila.length - 1 &&
          impressaoConfig?.comando_zpl_intermediario
        ) {
          txtLegacy += `${impressaoConfig.comando_zpl_intermediario}\n`;
        }
        if (i == fila.length - 1 && impressaoConfig?.comando_zpl_final) {
          txtLegacy += `${impressaoConfig.comando_zpl_final}\n`;
        }
        txtLegacy += `${zplLimpoLegacy}\n`;
        txtLegacy += `^XZ\n\n`;
        indiceAtual.current++;
        tagsImpressasNum.current++;
      }
      setArquivosLegacy(
        downloadImpressaoLegacy(txtLegacy, impressaoConfig.app_id),
      );
      handleAtivarCardConfirmarImpressao();
      return;
    }

    aguardandoResultado.current = true;
    enviarMensagem("FL"); // Finalizar leitura de calibração se necessário
    enviarMensagem(`P:${impressaoConfig?.potencia_padrao || "2"}`); // Configurar potência antes de enviar pacotes

    const pacotes = dividirZplEmPacotes(zplLimpo);
    // O ~DG nao pode ser cortado no meio de uma linha: ver
    // dividirDownloadGraficoEmPacotes.
    const pacotes_logo = dividirDownloadGraficoEmPacotes(logo);

    if (!removeuLogo.current) {
      removeuLogo.current = true;
      if (logo.length)
        pacotes_logo.forEach((p) =>
          enviarMensagem(formarPacoteParaEnvio(p, false, item.tag)),
        );
    }

    if (impressaoConfig?.gravacao_reversa) {
      enviarMensagem(formarPacoteParaEnvio("^XA"));
      if (indiceAtual.current === 0 && impressaoConfig.comando_zpl_inicial) {
        enviarMensagem(
          formarPacoteParaEnvio(impressaoConfig.comando_zpl_inicial),
        );
      }
      if (
        indiceAtual.current > 0 &&
        indiceAtual.current < fila.length - 1 &&
        impressaoConfig.comando_zpl_intermediario
      ) {
        enviarMensagem(
          formarPacoteParaEnvio(impressaoConfig.comando_zpl_intermediario),
        );
      }
      if (
        indiceAtual.current === fila.length - 1 &&
        impressaoConfig.comando_zpl_final
      ) {
        enviarMensagem(
          formarPacoteParaEnvio(impressaoConfig.comando_zpl_final),
        );
      }
      pacotes.forEach((p) => enviarMensagem(formarPacoteParaEnvio(p)));
      enviarMensagem(formarPacoteParaEnvio("^XZ"));
      return;
    }
    enviarMensagem(formarPacoteParaEnvio("^XA", false, item.tag));
    if (indiceAtual.current === 0 && impressaoConfig?.comando_zpl_inicial) {
      enviarMensagem(
        formarPacoteParaEnvio(
          impressaoConfig?.comando_zpl_inicial,
          false,
          item.tag,
        ),
      );
    }
    if (
      indiceAtual.current > 0 &&
      indiceAtual.current < fila.length - 1 &&
      impressaoConfig?.comando_zpl_intermediario
    ) {
      enviarMensagem(
        formarPacoteParaEnvio(
          impressaoConfig?.comando_zpl_intermediario,
          false,
          item.tag,
        ),
      );
    }
    if (
      indiceAtual.current === fila.length - 1 &&
      impressaoConfig?.comando_zpl_final
    ) {
      enviarMensagem(
        formarPacoteParaEnvio(
          impressaoConfig?.comando_zpl_final,
          false,
          item.tag,
        ),
      );
    }
    pacotes.forEach((p) =>
      enviarMensagem(formarPacoteParaEnvio(p, false, item.tag)),
    );
    enviarMensagem(formarPacoteParaEnvio("^XZ", false, item.tag));
  };

  const dividirZplEmPacotes = (zpl: string): string[] => {
    const partes: string[] = [];
    for (let i = 0; i < zpl.length; i += 500) {
      partes.push(zpl.slice(i, i + 500));
    }
    return partes;
  };

  // Escreve os itens na base e contabiliza as falhas. Chamada uma vez por
  // etiqueta ou, no modo "salvar no final", uma vez com a impressão inteira.
  // Devolve false quando a gravação falhou, para o chamador não seguir em
  // frente achando que persistiu.
  const persistirNaBase = async (itens: ImpressaoItem[]) => {
    if (!itens.length) return true;
    setIsLoading(true);
    try {
      await salvarItensNaBase(itens, reimpressao);
      return true;
    } catch (error) {
      const motivo =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        "erro desconhecido";
      console.error(
        "[salvarNaBase] falha após retries — itens impressos mas não persistidos:",
        itens.map((i) => ({ tag: i.tag, codigo: i.codigo })),
        error,
      );
      // Erro de categoria traz só os itens que ficaram de fora; os demais do
      // lote foram salvos.
      const naoSalvos: ImpressaoItem[] =
        (error as any)?.itensNaoSalvos ?? itens;
      for (const item of naoSalvos) {
        falhasSalvamentoRef.current.push({
          tag: item.tag,
          codigo: item.codigo,
          motivo: String(motivo),
        });
      }
      alert.error(
        `Falha ao salvar ${naoSalvos.length === 1 ? "etiqueta" : "etiquetas"} na base: ${motivo}`,
      );
      return false;
    } finally {
      setIsLoading(false);
      setCardConfirmarImpressao(false);
    }
  };

  /** Grava o lote que ficou pendente. No modo por etiqueta não há nada a fazer. */
  const descarregarSalvamentoPendente = async () => {
    const pendentes = itensPendentesRef.current;
    if (!pendentes.length) return;
    itensPendentesRef.current = [];
    await persistirNaBase(pendentes);
  };

  /** Joga fora o lote pendente. Só faz sentido quando o operador cancela a
   *  impressão: nesse modo os itens nunca chegaram a ser gravados. */
  const descartarSalvamentoPendente = () => {
    itensPendentesRef.current = [];
  };

  const salvarNaBase = async (itens: ImpressaoItem[]) => {
    // O histórico continua sendo atualizado etiqueta a etiqueta nos dois
    // modos; o que muda é só o momento da escrita na base.
    let persistiu = true;
    if (impressaoConfig?.salvar_itens_no_final) {
      itensPendentesRef.current.push(...itens);
    } else {
      persistiu = await persistirNaBase(itens);
    }

    if (persistiu) {
      try {
        if (historicoCriado && impressaoConfig?.gravacao_reversa) {
          const updatedHistorico: historicoItemResponse = {
            ...historicoCriado,
            erros,
          };
          let indexItem = updatedHistorico.ordem_itens_tratados.findIndex(
            (itemTratado) => itemTratado._id === itens[0]._id,
          );
          if (indexItem !== -1) {
            updatedHistorico.ordem_itens_tratados[indexItem] = {
              ...updatedHistorico.ordem_itens_tratados[indexItem],
              epc: itens[0].tag || "",
            };
            await atualizarHistorico(updatedHistorico);
          }
        }
      } catch (error) {
        console.error("Erro ao atualizar histórico:", error);
      }
    }
  };

  const finalizarImpressao = async (sucesso: boolean = true) => {
    // Rede de segurança: se a impressão terminou por um caminho que não passou
    // pelo card de confirmação, o lote pendente ainda tem que ir para a base —
    // as etiquetas já saíram da impressora. Quem cancela descarta o lote antes.
    await descarregarSalvamentoPendente();
    const falhas = falhasSalvamentoRef.current;
    setFila([]);
    indiceAtual.current = 0;
    aguardandoResultado.current = false;
    tentativas.current = 0;
    tagsImpressasNum.current = 0;
    removeuLogo.current = false;
    blallEnviado.current = false;
    blallExecutado.current = false;
    clrqEnviado.current = false;
    if (blallTimeoutId.current) {
      clearTimeout(blallTimeoutId.current);
      blallTimeoutId.current = null;
    }
    setImpressaoPausada(false);
    setCardConfirmarImpressao(false);
    setEstaImprimindo(false);
    setDeuErro(false);
    setCriouHistorico(false);
    setCardAlterarItens(false);
    setReimpressao(false);
    setArquivosLegacy(null);
    falhasSalvamentoRef.current = [];

    if (falhas.length > 0) {
      const amostra = falhas
        .slice(0, 5)
        .map((f) => `${f.codigo}/${f.tag}`)
        .join(", ");
      const sufixo = falhas.length > 5 ? ` (+${falhas.length - 5})` : "";
      console.error(
        `[finalizarImpressao] ${falhas.length} etiqueta(s) impressa(s) sem salvar na base:`,
        falhas,
      );
      alert.error(
        `${falhas.length} etiqueta(s) impressa(s) mas não salva(s) na base: ${amostra}${sufixo}. Verifique o console.`,
      );
    }
    if (sucesso) {
      if (nome_da_ordem) {
        definirOrdemComoImpressa(nome_da_ordem);
      }
      if (impressaoConfig?.webhook_ordem_finalizada) {
        try {
          await axios.post(impressaoConfig.webhook_ordem_finalizada, {
            nome_da_ordem: historicoCriado?.nome_da_ordem,
            estado_da_ordem: historicoCriado?.estado_da_ordem,
            hardware_id: historicoCriado?.hardware_id,
            id_conta: historicoCriado?.id_conta,
            id_usuario: historicoCriado?.id_usuario,
            etiquetas_impressas: historicoCriado?.etiquetas_impressas,
            etiquetas_gravadas: historicoCriado?.etiquetas_gravadas,
            etiquetas_salvas: historicoCriado?.etiquetas_salvas,
            etiquetas_total: historicoCriado?.ordem_itens_tratados,
            data_criacao: historicoCriado?.createdAt,
            data_ultima_atualizacao: historicoCriado?.updatedAt,
          });
        } catch (error) {
          console.error("Erro ao enviar webhook de ordem finalizada:", error);
        }
      }

      if (falhas.length === 0) {
        alert.success(t("impressao.finishedSuccess"));
      } else {
        alert.warning(
          `Impressão finalizada com ${falhas.length} falha(s) de salvamento.`,
        );
      }
    }
  };

  useEffect(() => {
    return () => {
      finalizarImpressao();
    };
  }, []);

  return (
    <ImpressaoContext.Provider
      value={{
        adicionarItensNaFila,
        fila,
        estaImprimindo,
        tagsImpressasNum,
        toggleImpressaoPausada,
        impressaoPausada,
        lerEtiqueta,
        finalizarImpressao,
        descartarSalvamentoPendente,
        reimprimirItem,
        regravarEtiqueta,
        deuErro,
        erroMensagem,
        cardConfirmarImpressao,
        arquivosLegacy,
        setCardConfirmarImpressao,
        handleAlterarItens,
        cardAlterarItens,
        setFila,
        setItensConfirmadosParaRemover,
        setItensParaRemocao,
        itensConfirmadosParaRemover,
        itensParaRemocao,
      }}
    >
      {children}
      <Impressao />
      <ModalItensNaoInventariados
        open={modalInventarioAberto}
        onOpenChange={setModalInventarioAberto}
      />
    </ImpressaoContext.Provider>
  );
}

type EtiquetaImpressaoData = {
  epc: string;
  estado: string;
};

export function useImpressao() {
  const context = useContext(ImpressaoContext);
  if (!context) {
    throw new Error("useImpressao deve ser usado dentro de ImpressaoProvider");
  }
  return context;
}

// ---- Estado badge helper ----
type ImpressaoEstado = "impresso" | "imprimindo" | "erro" | "aguardando";

function EstadoBadge({
  estado,
  erroMensagem,
}: {
  estado: ImpressaoEstado;
  erroMensagem: string;
}) {
  const variants: Record<ImpressaoEstado, { cls: string; icon: any; label: string }> = {
    impresso: {
      cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
      icon: Check,
      label: "Impresso",
    },
    imprimindo: {
      cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
      icon: Loader2,
      label: "Enviando",
    },
    erro: {
      cls: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
      icon: AlertTriangle,
      label: erroMensagem || "Erro",
    },
    aguardando: {
      cls: "bg-muted text-muted-foreground border-border",
      icon: Clock,
      label: "Aguardando",
    },
  };
  const v = variants[estado];
  const Icon = v.icon;
  return (
    <Badge variant="outline" className={`${v.cls} gap-1`}>
      <Icon className={`h-3 w-3 ${estado === "imprimindo" ? "animate-spin" : ""}`} />
      <span className="truncate max-w-[200px]">{v.label}</span>
    </Badge>
  );
}

function StatBox({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent: "emerald" | "amber" | "muted";
}) {
  const accentMap = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    muted: "text-foreground",
  };
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className={`text-2xl font-bold leading-tight ${accentMap[accent]}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
        {label}
      </div>
    </div>
  );
}

function Impressao() {
  const context = useImpressao();

  const {
    fila,
    estaImprimindo,
    tagsImpressasNum,
    toggleImpressaoPausada,
    impressaoPausada,
    lerEtiqueta,
    finalizarImpressao,
    descartarSalvamentoPendente,
    reimprimirItem,
    regravarEtiqueta,
    deuErro,
    erroMensagem,
    cardConfirmarImpressao,
    setCardConfirmarImpressao,
    arquivosLegacy,
    handleAlterarItens,
    cardAlterarItens,
    setFila,
    itensParaRemocao,
    setItensParaRemocao,
  } = context;
  const { salvarItensNaBase, apagarItensNaBase } = useItem();
  const { isLoading } = useGlobalLoader();

  const columns: ColumnDef<EtiquetaImpressaoData>[] = [
    {
      accessorKey: "epc",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.getValue("epc")}</span>
      ),
    },
    {
      accessorKey: "estado",
      header: "Estado",
      cell: ({ row }) => (
        <EstadoBadge
          estado={row.getValue("estado") as ImpressaoEstado}
          erroMensagem={erroMensagem}
        />
      ),
    },
  ];

  const columnsApagarItens: ColumnDef<ImpressaoItem>[] = [
    {
      accessorKey: "tag",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.getValue("tag")}</span>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Ações</div>,
      cell: ({ row }) => {
        const epc = row.getValue("tag") as string;
        const naFila = fila.some((item) => item.tag === epc);
        return (
          <div className="text-right">
            <Button
              variant={naFila ? "destructive" : "outline"}
              size="sm"
              onClick={() => {
                if (!naFila) {
                  const item = itensParaRemocao.find((i) => i.tag === epc);
                  if (item) {
                    apagarItensNaBase([item]);
                    setItensParaRemocao((prev) =>
                      prev.filter((i) => i.tag !== epc),
                    );
                    setFila((prev) => [...prev, item]);
                  }
                } else {
                  const itemEncontrado = fila.find((i) => i.tag === epc);
                  if (itemEncontrado) {
                    apagarItensNaBase([itemEncontrado]);
                  }
                  setFila((prev) => prev.filter((i) => i.tag !== epc));
                  setItensParaRemocao((prev) =>
                    prev.filter((i) => i.tag !== epc),
                  );
                  // O item removido é um impresso (a tabela só lista impressos).
                  // Decrementa para manter slice(0, tagsImpressasNum) apontando
                  // só para os impressos restantes — senão um item não impresso
                  // entraria no recorte e seria salvo indevidamente.
                  tagsImpressasNum.current = Math.max(
                    0,
                    tagsImpressasNum.current - 1,
                  );
                }
                alert.success(`Etiqueta ${epc} removida da base.`);
              }}
            >
              {naFila ? (
                <>
                  <Trash2 className="h-3.5 w-3.5" />
                  Remover da base
                </>
              ) : (
                <>
                  <RotateCw className="h-3.5 w-3.5" />
                  Restaurar
                </>
              )}
            </Button>
          </div>
        );
      },
    },
  ];

  if (!estaImprimindo) return null;

  const total = fila.length;
  const impressas = tagsImpressasNum.current;
  const restante = Math.max(0, total - impressas);
  const percent = total > 0 ? Math.round((impressas / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-background/80 backdrop-blur-sm">
      <Card className="w-full max-w-3xl shadow-xl flex flex-col max-h-[92vh] overflow-hidden">
        <CardHeader className="border-b">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full shrink-0 ${
                  deuErro
                    ? "bg-red-500/10"
                    : impressaoPausada
                      ? "bg-amber-500/10"
                      : "bg-primary/10"
                }`}
              >
                <Printer
                  className={`h-5 w-5 ${
                    deuErro
                      ? "text-red-500"
                      : impressaoPausada
                        ? "text-amber-500"
                        : "text-primary"
                  }`}
                />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base">Imprimindo etiquetas</CardTitle>
                <CardDescription className="text-xs">
                  {deuErro
                    ? "Erro durante a impressão — verifique abaixo."
                    : impressaoPausada
                      ? "Impressão pausada."
                      : "Suas etiquetas estão sendo enviadas para a impressora."}
                </CardDescription>
              </div>
            </div>
            <Badge
              variant="outline"
              className={
                deuErro
                  ? "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20"
                  : impressaoPausada
                    ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
                    : "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20"
              }
            >
              {deuErro ? "Erro" : impressaoPausada ? "Pausada" : "Imprimindo"}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col gap-4 overflow-auto py-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            <StatBox label="Impressas" value={impressas} accent="emerald" />
            <StatBox label="Restantes" value={restante} accent="amber" />
            <StatBox label="Total" value={total} accent="muted" />
          </div>

          {/* Progresso */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-medium">{percent}%</span>
            </div>
            <Progress value={percent} />
          </div>

          {/* Tabela */}
          <div className="rounded-md border overflow-hidden">
            <DataTable
              autoResetPageIndex={false}
              columns={columns}
              data={(() => {
                const itensNaoImpressos = fila
                  .slice(tagsImpressasNum.current)
                  .map((item, index) => ({
                    epc: item.tag,
                    estado:
                      index === 0
                        ? deuErro
                          ? "erro"
                          : "imprimindo"
                        : "aguardando",
                  }));
                const itensImpressos = fila
                  .slice(0, tagsImpressasNum.current)
                  .map((item) => ({
                    epc: item.tag,
                    estado: "impresso" as const,
                  }));
                return [...itensNaoImpressos, ...itensImpressos];
              })()}
            />
          </div>
        </CardContent>

        <CardFooter className="border-t pt-4 flex flex-wrap gap-2 justify-end">
          <Button
            disabled={isLoading}
            variant="outline"
            size="sm"
            onClick={lerEtiqueta}
          >
            <TagIcon className="h-4 w-4" />
            Ler etiqueta
          </Button>
          <Button
            disabled={isLoading}
            variant="outline"
            size="sm"
            onClick={regravarEtiqueta}
          >
            <Edit3 className="h-4 w-4" />
            Regravar
          </Button>
          <Button
            disabled={isLoading}
            variant="outline"
            size="sm"
            onClick={reimprimirItem}
          >
            <RotateCw className="h-4 w-4" />
            Reimprimir
          </Button>
          <Button
            disabled={isLoading}
            variant={impressaoPausada ? "default" : "secondary"}
            size="sm"
            onClick={toggleImpressaoPausada}
          >
            {impressaoPausada ? (
              <>
                <Play className="h-4 w-4" />
                Retomar
              </>
            ) : (
              <>
                <Pause className="h-4 w-4" />
                Pausar
              </>
            )}
          </Button>
          <Button
            disabled={isLoading}
            variant="destructive"
            size="sm"
            onClick={() => setCardConfirmarImpressao(true)}
          >
            <X className="h-4 w-4" />
            Encerrar
          </Button>
        </CardFooter>
      </Card>

      {/* Sub-dialog: confirmar finalização */}
      {cardConfirmarImpressao && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/70 backdrop-blur-sm p-4">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Suas etiquetas foram impressas corretamente?
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Confirme pra salvar na base. Se algumas etiquetas saíram
                    erradas, use "Alterar itens".
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            {arquivosLegacy && (
              <CardContent className="pt-0">
                <div className="rounded-md border bg-muted/40 p-3 space-y-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Arquivos da impressão</p>
                    <p className="text-xs text-muted-foreground">
                      O download é automático. Caso não tenha ocorrido, baixe os
                      arquivos abaixo. Os dois devem ficar na mesma pasta. O
                      .bat é o que envia o ZPL para a impressora.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => baixarArquivosLegacy(arquivosLegacy)}
                    >
                      <Download className="h-4 w-4" />
                      Baixar os dois arquivos
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        baixarArquivoGerado(
                          arquivosLegacy.txt.conteudo,
                          arquivosLegacy.txt.nome,
                        )
                      }
                    >
                      <Download className="h-4 w-4" />
                      {arquivosLegacy.txt.nome}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        baixarArquivoGerado(
                          arquivosLegacy.bat.conteudo,
                          arquivosLegacy.bat.nome,
                        )
                      }
                    >
                      <Download className="h-4 w-4" />
                      {arquivosLegacy.bat.nome}
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
            <CardFooter className="flex flex-wrap gap-2 justify-end">
              <Button
                disabled={isLoading}
                variant="destructive"
                size="sm"
                onClick={async () => {
                  // No modo "salvar no final" esses itens ainda não foram
                  // gravados; descartar o lote evita salvar só para apagar.
                  descartarSalvamentoPendente();
                  const itensImpressos = fila.slice(
                    0,
                    tagsImpressasNum.current,
                  );
                  if (itensImpressos.length > 0) {
                    await apagarItensNaBase(itensImpressos);
                  }
                  finalizarImpressao(false);
                }}
              >
                <X className="h-4 w-4" />
                Cancelar
              </Button>
              <Button
                disabled={isLoading}
                variant="outline"
                size="sm"
                onClick={() => handleAlterarItens()}
              >
                <Edit3 className="h-4 w-4" />
                Alterar itens
              </Button>
              <Button
                disabled={isLoading}
                size="sm"
                onClick={async () => {
                  setCardConfirmarImpressao(false);
                  const itensImpressos = fila.slice(
                    0,
                    tagsImpressasNum.current,
                  );
                  // Este recorte já é a lista definitiva do que deve ir para a
                  // base; descartar o lote pendente evita que um item removido
                  // em "Alterar itens" volte pelo flush de finalizarImpressao.
                  descartarSalvamentoPendente();
                  await salvarItensNaBase(itensImpressos);
                  finalizarImpressao(true);
                }}
              >
                <Check className="h-4 w-4" />
                Confirmar
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Sub-dialog: alterar itens */}
      {cardAlterarItens && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/70 backdrop-blur-sm p-4">
          <Card className="w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
            <CardHeader className="border-b">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Apague os itens com defeito
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Marque pra remover da base as etiquetas que não saíram
                    corretamente. Depois clique em "Confirmar alterações".
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-auto py-4">
              <div className="rounded-md border overflow-hidden">
                <DataTable
                  columns={columnsApagarItens}
                  data={fila.slice(0, tagsImpressasNum.current)}
                />
              </div>
            </CardContent>
            <CardFooter className="border-t pt-4 justify-end">
              <Button
                disabled={isLoading}
                size="sm"
                onClick={async () => {
                  setCardConfirmarImpressao(false);
                  descartarSalvamentoPendente();
                  await salvarItensNaBase(
                    fila.slice(0, tagsImpressasNum.current),
                  );
                  finalizarImpressao(true);
                }}
              >
                <Check className="h-4 w-4" />
                Confirmar alterações
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
}
